import { ref, onMounted, computed } from '@vue/composition-api'
import store from '@/store'
import axios from '@axios'
import { currency } from '@core/utils/filter'
import { getUserData } from '@/auth/utils'

export default function useDashboardAfiliado() {
  // Estados reativos
  const isLoading = ref(false)
  const comissoesData = ref([])
  const afiliacoesData = ref([])
  const statsData = ref({
    totalPedidos: 0,
    totalVendas: 0,
    totalPendente: 0,
    totalCancelado: 0,
    comissaoTotal: 0,
    comissaoPaga: 0,
    comissaoAPagar: 0,
    produtosVendidos: 0,
  })

  // Função para calcular estatísticas
  const calculateStats = () => {
    const comissoes = comissoesData.value
    
    let totalPedidos = 0
    let totalVendas = 0
    let totalPendente = 0
    let totalCancelado = 0
    let comissaoTotal = 0
    let comissaoPaga = 0
    let comissaoAPagar = 0
    let produtosVendidos = 0

    comissoes.forEach(comissao => {
      const pedido = comissao.pedido_item?.pedido
      const item = comissao.pedido_item?.item
      const valorComissao = Number(comissao.comissao_afiliado) || 0
      const valorTotalItem = Number(comissao.pedido_item?.valor_total_item) || 0
      const quantidade = Number(comissao.pedido_item?.quantidade) || 0
      
      console.log('Pedido:', pedido)
      console.log('Item:', item?.item_nome)
      console.log('Comissão:', valorComissao)
      console.log('Pago:', comissao.pago)
      
      totalPedidos += 1
      comissaoTotal += valorComissao
      produtosVendidos += quantidade

      // Comissões pagas/a pagar
      if (comissao.pago) {
        comissaoPaga += valorComissao
      } else {
        comissaoAPagar += valorComissao
      }

      // Status dos pedidos
      if (pedido?.status === 'Paid' || pedido?.status === 'Completed') {
        totalVendas += valorTotalItem
        console.log('Venda confirmada:', valorTotalItem)
      } else if (pedido?.status === 'Pending' || pedido?.status === 'WAITING_PAYMENT') {
        totalPendente += valorTotalItem
      } else if (pedido?.status === 'Canceled') {
        totalCancelado += valorTotalItem
      }
    })

    statsData.value = {
      totalPedidos,
      totalVendas,
      totalPendente,
      totalCancelado,
      comissaoTotal,
      comissaoPaga,
      comissaoAPagar,
      produtosVendidos,
    }

    console.log('Estatísticas calculadas:', statsData.value)
  }

  // Função para buscar afiliações do usuário
  const fetchAfiliacao = async () => {
    try {
      const userData = getUserData()
      const userId = userData?.id

      if (!userId) {
        console.error('ID do usuário não encontrado')
        return
      }

      const response = await store.dispatch('appLinkAfiliado/findAll', {
        q: '',
        perPage: 1000,
        page: 1,
        sortBy: 'id',
        sortDesc: true,
        ativo: null,
        id_categoria: null,
      })

      const { linksAfiliado } = response.data
      // Filtrar apenas as afiliações do usuário atual
      afiliacoesData.value = linksAfiliado.filter(item => item.usuario_inclusao?.id === userId)
    } catch (error) {
      console.error('Erro ao buscar afiliações:', error)
    }
  }

  // Função para buscar comissões do afiliado
  const fetchComissoes = async () => {
    try {
      isLoading.value = true
      const userData = getUserData()
      const userId = userData?.afiliado.id || null

      if (!userId) {
        console.error('ID do usuário não encontrado')
        return
      }

      const response = await axios.get(`/comissao/afiliado/${userId}`)
      comissoesData.value = response.data.data || []
      calculateStats()
    } catch (error) {
      console.error('Erro ao buscar comissões:', error)
    } finally {
      isLoading.value = false
    }
  }

  // Dados computados para estatísticas
  const estatisticasAfiliado = computed(() => [
    {
      title: 'Total de Vendas',
      value: statsData.value.totalPedidos.toString(),
      icon: 'ShoppingCartIcon',
      color: 'primary',
    },
    {
      title: 'Comissões Totais',
      value: currency(statsData.value.comissaoTotal),
      icon: 'DollarSignIcon',
      color: 'success',
    },
    {
      title: 'A Receber',
      value: currency(statsData.value.comissaoAPagar),
      icon: 'ClockIcon',
      color: 'warning',
    },
    {
      title: 'Produtos Vendidos',
      value: statsData.value.produtosVendidos.toString(),
      icon: 'PackageIcon',
      color: 'info',
    },
  ])

  // Dados computados para progresso baseado em dados reais
  const progressoReal = computed(() => {
    const comissoes = comissoesData.value
    const produtosUnicos = new Set(comissoes.map(c => c.pedido_item?.id_item)).size
    console.log('Produtos únicos vendidos:', produtosUnicos)
    const { comissaoTotal, comissaoPaga } = statsData.value

    return {
      produtosVendidos: produtosUnicos,
      comissaoRecebida: comissaoPaga,
      comissaoTotal,
      taxaConversao: comissoes.length > 0
        ? ((comissoes.filter(c => c.pedido_item?.pedido?.status === 'Paid' || c.pedido_item?.pedido?.status === 'Completed').length / comissoes.length) * 100).toFixed(1)
        : 0,
    }
  })

  // Lista compacta das afiliações do usuário
  const minhasAfiliacao = computed(() => afiliacoesData.value.slice(0, 6).map(afiliacao => ({
    id: afiliacao.id,
    produto: afiliacao.item?.item_nome || 'Produto não identificado',
    status: afiliacao.aprovado,
    statusAtivo: afiliacao.item?.ativo === 1,
    comissaoPercent: afiliacao.item?.comissao ? (afiliacao.item.comissao - 2) : 0,
    preco: afiliacao.item?.tabela_preco_itens?.[0]?.preco_venda || 0,
    vendedor: afiliacao.item?.vendedores?.usuario?.cliente?.nome || 'N/A',
    urlAfiliado: afiliacao.url_afiliado,
    aprovado: afiliacao.aprovado === 'Aprovado' && afiliacao.item?.ativo === 1,
  })))

  // Lista dos últimos produtos vendidos
  const ultimasVendas = computed(() => comissoesData.value.slice(0, 5).map(comissao => ({
    produto: comissao.pedido_item?.item?.item_nome || 'Produto não identificado',
    orderId: comissao.pedido_item?.pedido?.order_id || 'N/A',
    quantidade: comissao.pedido_item?.quantidade || 0,
    comissao: currency(comissao.comissao_afiliado || 0),
    valorTotal: currency(comissao.pedido_item?.valor_total_item || 0),
    status: comissao.pedido_item?.pedido?.status || 'Desconhecido',
    pago: comissao.pago ? 'Sim' : 'Não',
  })))

  // Dados computados para o próximo pagamento baseado em dados reais
  const proximoPagamentoReal = computed(() => {
    const valorAPagar = statsData.value.comissaoAPagar
    const proximaData = new Date()
    proximaData.setDate(proximaData.getDate() + 30) // 30 dias a partir de hoje
    
    return {
      data: proximaData.toLocaleDateString('pt-BR'),
      valor: currency(valorAPagar),
    }
  })

  // Dados computados para estatísticas de desempenho
  const estatisticasDesempenho = computed(() => {
    const comissoes = comissoesData.value
    const vendas = comissoes.filter(c => c.pedido_item?.pedido?.status === 'Paid' || c.pedido_item?.pedido?.status === 'Completed')
    const mediaComissao = vendas.length > 0 ? vendas.reduce((acc, c) => acc + (Number(c.comissao_afiliado) || 0), 0) / vendas.length : 0
    
    return [
      {
        label: 'Comissão Média',
        value: currency(mediaComissao),
        icon: 'TrendingUpIcon',
        color: 'success',
      },
      {
        label: 'Taxa de Conversão',
        value: `${progressoReal.value.taxaConversao}%`,
        icon: 'TargetIcon',
        color: 'primary',
      },
      {
        label: 'Comissões Pagas',
        value: currency(statsData.value.comissaoTotal),
        icon: 'CheckCircleIcon',
        color: 'info',
      },
      {
        label: 'Produtos Únicos',
        value: progressoReal.value.produtosVendidos.toString(),
        icon: 'BoxIcon',
        color: 'warning',
      },
    ]
  })

  // Função para processar dados do gráfico de comissões por mês
  const processChartData = () => {
    const comissoes = comissoesData.value
    const currentYear = new Date().getFullYear()
    
    // Debug dos dados recebidos
    console.log('Processando dados do gráfico:', {
      totalComissoes: comissoes.length,
      anoAtual: currentYear,
    })
    
    // Inicializar dados mensais para todos os 12 meses
    const monthlyData = Array.from({ length: 12 }, (_, index) => ({
      month: index,
      value: 0,
      count: 0,
    }))
    
    // Processar comissões por mês baseado na data_inclusao
    comissoes.forEach((comissao, index) => {
      if (comissao.data_inclusao) {
        const date = new Date(comissao.data_inclusao)
        const year = date.getFullYear()
        const month = date.getMonth() // 0-11
        const valorComissao = Number(comissao.comissao_afiliado) || 0
        
        // Debug dos dados de cada comissão
        if (index < 3) { // Log apenas as primeiras 3 para não poluir o console
          console.log(`Comissão ${index + 1}:`, {
            data_inclusao: comissao.data_inclusao,
            ano: year,
            mes: month,
            valor: valorComissao,
            incluida: year === currentYear,
          })
        }
        
        if (year === currentYear) {
          monthlyData[month].value += valorComissao
          monthlyData[month].count += 1
        }
      }
    })
    
    // Debug dos dados finais
    console.log('Dados mensais processados:', monthlyData.map((item, index) => ({
      mes: index + 1,
      valor: item.value,
      quantidade: item.count,
    })))
    
    return monthlyData
  }

  // Dados computados para o gráfico de comissões
  const chartDataComissoes = computed(() => {
    const monthlyData = processChartData()
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    
    return {
      labels: monthNames,
      datasets: [
        {
          label: 'Comissões (R$)',
          data: monthlyData.map(item => Number(item.value.toFixed(2))),
          borderColor: '#7367f0',
          backgroundColor: 'rgba(115, 103, 240, 0.1)',
          borderWidth: 2,
          pointRadius: 4,
          pointBackgroundColor: '#7367f0',
          pointBorderColor: '#fff',
          tension: 0.4,
          fill: true,
        },
      ],
    }
  })

  // Opções do gráfico com formatação brasileira
  const chartOptionsComissoes = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          display: true,
          fontSize: 10,
          fontColor: '#b9b9c3',
          callback: value => `R$ ${value.toFixed(2).replace('.', ',')}`,
        },
        gridLines: {
          display: true,
          drawBorder: false,
          color: 'rgba(200, 200, 200, 0.2)',
        },
      }],
      xAxes: [{
        ticks: {
          display: true,
          fontSize: 10,
          fontColor: '#b9b9c3',
        },
        gridLines: {
          display: false,
          drawBorder: false,
        },
      }],
    },
    elements: {
      point: {
        hoverRadius: 6,
      },
    },
    tooltips: {
      enabled: true,
      mode: 'index',
      intersect: false,
      callbacks: {
        label: tooltipItem => {
          const value = tooltipItem.yLabel
          return `Comissões: R$ ${value.toFixed(2).replace('.', ',')}`
        },
      },
    },
  }))

  const chartDataCliques = ref({
    labels: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'],
    datasets: [
      {
        label: 'Cliques',
        data: [12, 25, 35, 60, 45, 30, 20],
        borderColor: '#28c76f',
        backgroundColor: 'rgba(40, 199, 111, 0.1)',
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#28c76f',
        pointBorderColor: '#fff',
        tension: 0.4,
        fill: true,
      },
    ],
  })

  const chartOptionsCliques = ref({
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          display: true,
          fontSize: 10,
          fontColor: '#b9b9c3',
        },
        gridLines: {
          display: true,
          drawBorder: false,
          color: 'rgba(200, 200, 200, 0.2)',
        },
      }],
      xAxes: [{
        ticks: {
          display: true,
          fontSize: 10,
          fontColor: '#b9b9c3',
        },
        gridLines: {
          display: false,
          drawBorder: false,
        },
      }],
    },
    elements: {
      point: {
        hoverRadius: 6,
      },
    },
  })

  // Dados estáticos (podem ser integrados com APIs futuramente)
  const progresso = ref({
    divulgados: 3,
    divulgadosTotal: 10,
    comissaoAtual: 125,
    comissaoMeta: 500,
    indicacoes: 1,
    indicacoesTotal: 5,
  })

  const proximoPagamento = ref({
    data: '15/05/2025',
    valor: '2334,00',
  })

  const produtosDivulgar = ref([
    {
      nome: 'Curso Premium XYZ',
      comissao: '25,00',
      icon: 'Share2Icon',
      color: 'primary',
    },
    {
      nome: 'Kit Especial ABC',
      comissao: '15,00',
      icon: 'GiftIcon',
      color: 'success',
    },
    {
      nome: 'Assinatura Mensal',
      comissao: '10,00',
      icon: 'LinkIcon',
      color: 'info',
    },
  ])

  const centralAjuda = ref([
    {
      titulo: 'Onde vejo minhas afiliações?',
      descricao: 'Acesse a página de afiliações para gerenciar seus links e acompanhar o status.',
      icon: 'EyeIcon',
      color: 'primary',
      route: 'app-afiliado-produtos',
    },
    {
      titulo: 'Como receber minhas comissões?',
      descricao: 'Confira as formas de pagamento disponíveis e os prazos para recebimento.',
      icon: 'DollarSignIcon',
      color: 'success',
      route: 'app-financeiro-saque',
    },
    {
      titulo: 'Como solicito mais afiliações?',
      descricao: 'Saiba como solicitar novos produtos para divulgar e aumentar sua renda.',
      icon: 'PlusCircleIcon',
      color: 'info',
      route: 'app-adm-produto-afiliado-add',
    },
  ])

  const linksAtivos = ref([
    {
      id: 1,
      produto: 'Curso Premium XYZ',
      cliques: 15,
      conversao: '2%',
      link: 'https://yoursite.com/ref/user/xyz',
    },
    {
      id: 2,
      produto: 'Kit Especial ABC',
      cliques: 8,
      conversao: '1.5%',
      link: 'https://yoursite.com/ref/user/abc',
    },
    {
      id: 3,
      produto: 'Assinatura Mensal',
      cliques: 25,
      conversao: '3%',
      link: 'https://yoursite.com/ref/user/mensal',
    },
  ])

  // Lifecycle
  onMounted(() => {
    fetchComissoes()
    fetchAfiliacao()
  })

  return {
    // Estados
    isLoading,
    comissoesData,
    afiliacoesData,
    statsData,
    
    // Dados computados
    estatisticasAfiliado,
    progressoReal,
    minhasAfiliacao,
    ultimasVendas,
    proximoPagamentoReal,
    estatisticasDesempenho,
    
    // Dados dos gráficos
    chartDataComissoes,
    chartDataCliques,
    chartOptionsComissoes,
    chartOptionsCliques,
    
    // Dados estáticos (mantidos para compatibilidade)
    progresso,
    proximoPagamento,
    produtosDivulgar,
    centralAjuda,
    linksAtivos,
    
    // Métodos
    fetchComissoes,
    calculateStats,
  }
}
